// Phase 2: Permission Gates and Hooks
// File: src/components/common/PermissionGate.tsx

import React, { ReactNode } from 'react';
import { useEnhancedAuth } from '../../contexts/EnhancedAuthContext';
import { UserRole } from '../../types/RoleSystem';

interface PermissionGateProps {
  children: ReactNode;
  requiredPermissions?: string[];
  requiredRoles?: UserRole[];
  requireAllPermissions?: boolean; // If true, user must have ALL permissions. If false, ANY permission
  requireAllRoles?: boolean; // If true, user must have ALL roles. If false, ANY role
  fallback?: ReactNode;
  onAccessDenied?: () => void;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
  requireAllPermissions = true,
  requireAllRoles = false,
  fallback = null,
  onAccessDenied,
}) => {
  const { userDetails, hasPermission, getUserRole } = useEnhancedAuth();
  
  if (!userDetails) {
    onAccessDenied?.();
    return <>{fallback}</>;
  }

  const currentRole = getUserRole();
  
  // Check role requirements
  const hasRequiredRole = requiredRoles.length === 0 || (
    requireAllRoles
      ? requiredRoles.every(role => currentRole === role)
      : requiredRoles.some(role => currentRole === role)
  );

  // Check permission requirements
  const hasRequiredPermissions = requiredPermissions.length === 0 || (
    requireAllPermissions
      ? requiredPermissions.every(permission => hasPermission(permission))
      : requiredPermissions.some(permission => hasPermission(permission))
  );

  const hasAccess = hasRequiredRole && hasRequiredPermissions;

  if (!hasAccess) {
    onAccessDenied?.();
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Specialized Permission Gates
interface RoleGateProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  fallback?: ReactNode;
}

export const RoleGate: React.FC<RoleGateProps> = ({ children, allowedRoles, fallback = null }) => (
  <PermissionGate requiredRoles={allowedRoles} requireAllRoles={false} fallback={fallback}>
    {children}
  </PermissionGate>
);

export const SuperAdminGate: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback = null }) => (
  <RoleGate allowedRoles={['Super Admin']} fallback={fallback}>
    {children}
  </RoleGate>
);

export const AdminGate: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback = null }) => (
  <RoleGate allowedRoles={['Super Admin', 'Admin']} fallback={fallback}>
    {children}
  </RoleGate>
);

export const ManagerGate: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback = null }) => (
  <RoleGate allowedRoles={['Super Admin', 'Admin', 'Manager']} fallback={fallback}>
    {children}
  </RoleGate>
);

// Feature-specific Gates
export const DocumentManagementGate: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback = null }) => (
  <PermissionGate 
    requiredPermissions={['doc_view_own', 'doc_view_all', 'doc_company']} 
    requireAllPermissions={false}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
);

export const UserManagementGate: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback = null }) => (
  <PermissionGate 
    requiredPermissions={['user_manage_agency', 'user_manage_system']} 
    requireAllPermissions={false}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
);

export const CompanyDocumentsGate: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback = null }) => (
  <PermissionGate requiredPermissions={['doc_company']} fallback={fallback}>
    {children}
  </PermissionGate>
);
